import { leadTimeCompositionSnapshotSchema } from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  FORECAST_RUN_PURPOSES,
  FORECAST_RUN_STATUSES,
  LEAD_TIME_SOURCES,
  forecastRunMethodConfigSchema,
} from "@erp/shared-types";

// Read surfaces over the forecasting engine's own manifest
// (`im_forecast_runs`), its per-series inputs (`im_forecast_run_inputs`) and
// its output curve (`im_demand_forecasts`). The engine wrote all three long
// before anything displayed them; these three procedures are the buyer- and
// operator-facing view of a run: did it cover everything, what did it decide,
// and on what evidence.

export const ForecastRunPurposeSchema = z.enum(FORECAST_RUN_PURPOSES);
export const ForecastRunStatusSchema = z.enum(FORECAST_RUN_STATUSES);
/** Where a series' lead time came from: a ladder rung, a manual 'override',
 * or 'none' = flat fallback. Derived from the shared list so it tracks the
 * backend registry. */
export const ForecastLeadTimeSourceSchema = z.enum(LEAD_TIME_SOURCES);
export type ForecastLeadTimeSource = z.infer<
  typeof ForecastLeadTimeSourceSchema
>;

// ─── Run summary ────────────────────────────────────────────────────────────

export const ForecastRunSummarySchema = z.object({
  id: z.string().uuid(),
  asOfDate: z.string(),
  purpose: ForecastRunPurposeSchema,
  status: ForecastRunStatusSchema,
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  /** Wall-clock seconds; null while still running. */
  durationSeconds: z.number().nullable(),
  windowStart: z.string(),
  windowEnd: z.string(),
  historyMonths: z.number(),
  horizonMonths: z.number(),
  /**
   * The rest of the forecast settings the run used, as its manifest recorded
   * them. Null when the run recorded no complete method config.
   */
  methodConfig: forecastRunMethodConfigSchema.nullable(),
  engineVersion: z.string(),
  seriesCount: z.number(),
  forecastsWritten: z.number(),
  policiesUpdated: z.number(),
  /** Divisions the run was scoped to. */
  divisionCount: z.number(),
  triggeredBy: z.string().nullable(),
  /**
   * Why a run is 'failed'/'refused', or which divisions a 'partial' run held
   * back on a broken-input guard (lead-time coverage or demand-key coverage).
   */
  errorMessage: z.string().nullable(),
  byMethod: z.record(z.string(), z.number()),
  byClass: z.record(z.string(), z.number()),
  byLeadTimeSource: z.record(z.string(), z.number()),
  /**
   * Share of series that got no measured lead time (the flat fallback). The
   * headline data-quality number for a run; null when the run recorded no
   * lead-time breakdown (it never reached the engine loop).
   */
  unmeasuredLeadTimeShare: z.number().nullable(),
});
export type ForecastRunSummary = z.infer<typeof ForecastRunSummarySchema>;

// ─── List ───────────────────────────────────────────────────────────────────

export const ListForecastRunsInputSchema = z.object({
  purpose: ForecastRunPurposeSchema.optional(),
  status: ForecastRunStatusSchema.optional(),
  limit: z.number().int().min(1).max(100).optional().default(25),
});
export const ListForecastRunsResponseSchema = z.object({
  runs: z.array(ForecastRunSummarySchema),
});
export type ListForecastRunsInput = z.infer<typeof ListForecastRunsInputSchema>;
export type ListForecastRunsResponse = z.infer<
  typeof ListForecastRunsResponseSchema
>;

// ─── Detail ─────────────────────────────────────────────────────────────────

/**
 * One division's slice of a run, derived from the per-series input rows. A
 * division the run held back has no rows and so does not appear — which is
 * exactly what `heldBack` on the response reports instead.
 */
export const ForecastRunDivisionSchema = z.object({
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
  seriesCount: z.number(),
  warehouseCount: z.number(),
  /** Series that fell through the lead-time ladder to the flat fallback. */
  unmeasuredCount: z.number(),
  unmeasuredShare: z.number(),
  /** Median applied lead time in days across the division's series. */
  medianLeadTimeDays: z.number().nullable(),
  /** Total forecast quantity over the run's whole horizon. */
  forecastQty: z.number(),
});
export type ForecastRunDivision = z.infer<typeof ForecastRunDivisionSchema>;

export const GetForecastRunInputSchema = z.object({
  /** Omit for the most recent production run — the default landing view. */
  runId: z.string().uuid().optional(),
});

export const GetForecastRunResponseSchema = z.object({
  /** Null when the org has no forecast runs at all. */
  run: ForecastRunSummarySchema.nullable(),
  divisions: z.array(ForecastRunDivisionSchema),
  /**
   * Divisions in the run's scope that produced no series — either held back by
   * the coverage guard (a 'partial' run) or genuinely without demand history.
   */
  missingDivisions: z.array(
    z.object({
      divisionId: z.string().uuid(),
      divisionName: z.string().nullable(),
    }),
  ),
  /** Forecast quantity per horizon month, summed across every series. */
  horizonTotals: z.array(
    z.object({ periodStart: z.string(), forecastQty: z.number() }),
  ),
});
export type GetForecastRunInput = z.infer<typeof GetForecastRunInputSchema>;
export type GetForecastRunResponse = z.infer<
  typeof GetForecastRunResponseSchema
>;

// ─── Series ─────────────────────────────────────────────────────────────────

export const ForecastSeriesRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string().nullable(),
  divisionName: z.string().nullable(),
  productV3Key: z.string().nullable(),
  productName: z.string().nullable(),
  demandClass: z.string().nullable(),
  method: z.string().nullable(),
  /** Demand history the run consumed, oldest first (dense, zero-filled). */
  history: z.array(z.number()),
  historyStart: z.string(),
  /** Whether a buyer override edited that history. */
  overrideApplied: z.boolean(),
  /** The forward curve, oldest first — same length as the run's horizon. */
  forecast: z.array(z.number()),
  forecastQty: z.number(),
  leadTimeComposition: leadTimeCompositionSnapshotSchema.nullable().optional(),
  leadTimeDaysUsed: z.number().nullable(),
  leadTimeSource: ForecastLeadTimeSourceSchema.nullable(),
  leadTimeSampleN: z.number().nullable(),
  targetAvailability: z.number().nullable(),
});
export type ForecastSeriesRow = z.infer<typeof ForecastSeriesRowSchema>;

export const ListForecastSeriesInputSchema = z.object({
  /** Omit for the most recent production run, matching `get`. */
  runId: z.string().uuid().optional(),
  divisionId: z.string().uuid().optional(),
  warehouseId: z.string().uuid().optional(),
  /** Matches product v3 key or product name. */
  search: z.string().optional(),
  leadTimeSource: ForecastLeadTimeSourceSchema.optional(),
  demandClass: z.string().optional(),
  method: z.string().optional(),
  /** Only series whose lead time came from the flat fallback. */
  unmeasuredOnly: z.boolean().optional().default(false),
  sortBy: z
    .enum(["forecastQty", "productV3Key", "leadTimeDaysUsed"])
    .optional()
    .default("forecastQty"),
  sortDir: z.enum(["asc", "desc"]).optional().default("desc"),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(200).optional().default(50),
});

export const ListForecastSeriesResponseSchema = z.object({
  runId: z.string().uuid().nullable(),
  rows: z.array(ForecastSeriesRowSchema),
  totalRows: z.number(),
});
export type ListForecastSeriesInput = z.infer<
  typeof ListForecastSeriesInputSchema
>;
export type ListForecastSeriesResponse = z.infer<
  typeof ListForecastSeriesResponseSchema
>;

// ─── Demand matrix ──────────────────────────────────────────────────────────

/**
 * A second series to show beside the forecast in every month cell.
 *
 * `actuals` reads `im_forecast_accuracy`, which the scorer writes once a
 * forecast month has closed — so it only has anything to say about a run whose
 * horizon is in the past. `run` reads another run's own curve, which is how you
 * see what changed between two forecasts of the same months.
 *
 * An array because the periods can overlap: a run from six months ago has both
 * closed months (actuals) and months a newer run also covers.
 *
 * TODO(procurement): a third kind, an end-of-period projection for popular
 * SKUs, is wanted but not specified yet — hence the discriminated union rather
 * than a boolean pair.
 */
export const DemandComparisonSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("actuals") }),
  z.object({ kind: z.literal("run"), runId: z.string().uuid() }),
]);
export type DemandComparison = z.infer<typeof DemandComparisonSchema>;

/** At most three: past that, a cell has more numbers than a reader can use. */
export const DemandComparisonsSchema = z.array(DemandComparisonSchema).max(3);

export const DemandComparisonSeriesSchema = z.object({
  kind: z.enum(["actuals", "run"]),
  /** Which run, when `kind` is 'run'. */
  runId: z.string().uuid().nullable(),
  /**
   * Aligned to the response's `months`. Null where the comparison has nothing
   * for that month — an unscored future month, or a month outside the other
   * run's horizon. Null is not zero: "we do not know" versus "no demand".
   */
  monthQty: z.array(z.number().nullable()),
  /** Sum of the non-null months, so it matches what the row actually shows. */
  total: z.number(),
});
export type DemandComparisonSeries = z.infer<
  typeof DemandComparisonSeriesSchema
>;

// The buyer-facing read of a run: projected demand as a SKU x location x month
// grid. Deliberately separate from `listSeries` (the engine's own diagnostic
// view) because the questions differ — "how much of this SKU will division 4
// need in October" versus "what evidence produced this series".

/**
 * Row grain of the grid. `warehouse` is the grain the engine forecasts at, so
 * it needs no aggregation and is the fast default; the coarser grains sum
 * across the locations in scope.
 */
export const DemandRollupSchema = z.enum(["warehouse", "division", "sku"]);
export type DemandRollup = z.infer<typeof DemandRollupSchema>;

/**
 * Every dimension the grid can be sliced by. All of them narrow the scope of
 * the SQL, so a filtered view is strictly cheaper than the default one.
 */
export const DemandMatrixFilterSchema = z.object({
  /** Omit for the most recent production run, matching `get`. */
  runId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  /** `im_warehouses.type` — 'hub' | 'branch' in practice, free text in schema. */
  warehouseTypes: z.array(z.string()).optional(),
  /** Exact SKU keys, for the "pinned to these items" case (deep links). */
  skuKeys: z.array(z.string()).optional(),
  /** Substring match on the SKU code alone. `*` is a wildcard. */
  skuSearch: z.string().optional(),
  /** Substring match on the SKU code or its description. `*` is a wildcard. */
  textSearch: z.string().optional(),
  demandClasses: z.array(z.string()).optional(),
  methods: z.array(z.string()).optional(),
  leadTimeSources: z.array(ForecastLeadTimeSourceSchema).optional(),
  /**
   * `im_inventory_policies.stock_status` — 'stocked' / 'non_stock'. The reason
   * this filter exists: a forecast covers every SKU with demand history, most
   * of which a branch does not stock and never will.
   */
  stockStatuses: z.array(z.string()).optional(),
  /** `im_inventory_policies.popularity_code` — the A-G/N/R/S/V movement class. */
  popularityCodes: z.array(z.string()).optional(),
  /** Only series whose demand history a buyer edited. */
  overrideOnly: z.boolean().optional(),
  /** Inclusive YYYY-MM-01 bounds on the month columns. */
  monthFrom: z.string().optional(),
  monthTo: z.string().optional(),
  /**
   * Drop rows whose forecast is zero across every visible month. A forecast
   * covers every series in the run, and most of them are flat zero, so this is
   * the difference between a grid of blanks and a grid of demand.
   *
   * It is a filter rather than a display option because the row has to be gone
   * before the page is cut: hiding zeros client-side leaves short pages and a
   * total that disagrees with what is on screen.
   */
  hideEmpty: z.boolean().optional(),
});
export type DemandMatrixFilter = z.infer<typeof DemandMatrixFilterSchema>;

/**
 * `location` is (SKU, location) order and streams off an index; `total` and
 * `month` have to aggregate the whole scope before they can rank, so they cost
 * what the scope costs. That is the one performance cliff on this page and the
 * reason the default is `location`.
 */
export const DemandMatrixSortSchema = z.enum(["location", "total", "month"]);

/**
 * What the month cells show. `cost` values the same forecast quantities at a
 * reference unit cost per SKU — see the read service for exactly which cost,
 * and why it is per SKU rather than per SKU and location.
 */
export const DemandMeasureSchema = z.enum(["quantity", "cost"]);
export type DemandMeasure = z.infer<typeof DemandMeasureSchema>;

export const GetDemandMatrixInputSchema = DemandMatrixFilterSchema.extend({
  rollup: DemandRollupSchema.optional().default("warehouse"),
  measure: DemandMeasureSchema.optional().default("quantity"),
  comparisons: z.array(DemandComparisonSchema).max(3).optional(),
  /**
   * Prepend the period of the same length immediately before the horizon,
   * carrying the demand the run actually consumed for those months. Only
   * meaningful alongside an `actuals` comparison — on its own it would add
   * columns with nothing in them, since a run holds no forecast for the past.
   */
  includeHistory: z.boolean().optional(),
  sortBy: DemandMatrixSortSchema.optional().default("location"),
  sortDir: z.enum(["asc", "desc"]).optional().default("asc"),
  /** Index into the response's `months` when `sortBy` is 'month'. */
  // fallow-ignore-next-line code-duplication
  sortMonth: z.number().int().min(0).optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(200).optional().default(50),
});
export type GetDemandMatrixInput = z.infer<typeof GetDemandMatrixInputSchema>;
/**
 * The pre-parse shape, where the defaulted fields are still optional. Callers
 * building a request want this; the handler, which receives the parsed value,
 * gets {@link GetDemandMatrixInput}.
 */
export type GetDemandMatrixArgs = z.input<typeof GetDemandMatrixInputSchema>;

export const DemandMatrixRowSchema = z.object({
  /** `im_products_v3.key`. Null only on a legacy v2-keyed run. */
  sku: z.string().nullable(),
  skuLabel: z.string().nullable(),
  productName: z.string().nullable(),
  /** Null at grains coarser than the column. */
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  warehouseType: z.string().nullable(),
  /**
   * One entry per month in the response's `months`, same order. Null on a
   * history month: the run forecast the future, so there is no forecast to
   * show for a month that had already happened when it ran.
   */
  monthQty: z.array(z.number().nullable()),
  total: z.number(),
  /**
   * Reference unit cost used when the measure is `cost`; null when no supplier
   * product carries one, in which case that row's cost cells are blank rather
   * than zero — an unpriced SKU is unknown, not free.
   */
  unitCost: z.number().nullable(),
  /**
   * Series-level context, populated only at `warehouse` grain — a rolled-up row
   * covers many series and has no single class, method or lead time.
   */
  demandClass: z.string().nullable(),
  method: z.string().nullable(),
  leadTimeSource: ForecastLeadTimeSourceSchema.nullable(),
  leadTimeComposition: leadTimeCompositionSnapshotSchema.nullable().optional(),
  leadTimeDaysUsed: z.number().nullable(),
  overrideApplied: z.boolean().nullable(),
  /** Demand history the run consumed, oldest first; null when rolled up. */
  history: z.array(z.number()).nullable(),
  historyStart: z.string().nullable(),
  /** One entry per requested comparison, in the order they were requested. */
  comparisons: z.array(DemandComparisonSeriesSchema),
  /**
   * The run's forecast for the open month summed from its first day through
   * `monthToDate.through`, from the run's daily series. Null when any series
   * in the row has no daily forecast: a monthly forecast says nothing about
   * when in the month the demand lands. Absent when nothing is month-to-date.
   */
  forecastToDate: z.number().nullable().optional(),
});
export type DemandMatrixRow = z.infer<typeof DemandMatrixRowSchema>;

/**
 * The month being lived through, when its Actual is a partial month. The
 * accuracy ledger only scores closed months, so this one is read live up to
 * `through` and must be labelled month-to-date rather than read as a full month.
 */
export const MonthToDateSchema = z.object({
  /** YYYY-MM-01, one of `months`. */
  month: z.string(),
  /** YYYY-MM-DD, the day the figure runs to. */
  through: z.string(),
});
export type MonthToDate = z.infer<typeof MonthToDateSchema>;

/** One series' open month, day by day, for the month-to-date chart. */
export const GetMonthToDateDailyInputSchema = z.object({
  runId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  sku: z.string().min(1),
});
export type GetMonthToDateDailyInput = z.infer<
  typeof GetMonthToDateDailyInputSchema
>;

export const MonthToDateDaySchema = z.object({
  /** YYYY-MM-DD. */
  date: z.string(),
  /** Null when the run has no daily forecast for the series. */
  forecast: z.number().nullable(),
  /** Null after `through`: the day has not happened yet. */
  actual: z.number().nullable(),
});
export type MonthToDateDay = z.infer<typeof MonthToDateDaySchema>;

export const GetMonthToDateDailyResponseSchema = z.object({
  /** Null when today's month is not in the run's horizon. */
  monthToDate: MonthToDateSchema.nullable(),
  /** Every day of the open month; empty when there is no open month. */
  days: z.array(MonthToDateDaySchema),
});
export type GetMonthToDateDailyResponse = z.infer<
  typeof GetMonthToDateDailyResponseSchema
>;

export const GetDemandMatrixResponseSchema = z.object({
  /** Null when the org has no forecast run that produced output. */
  runId: z.string().uuid().nullable(),
  asOfDate: z.string().nullable(),
  /** The column axis: YYYY-MM-01, ascending, after any month-range filter. */
  months: z.array(z.string()),
  /**
   * How many of the leading `months` are history rather than horizon. Zero
   * unless `includeHistory` was set; the grid uses it to divide the axis.
   */
  historyMonthCount: z.number(),
  /** Set when an actuals comparison covers the current, still-open month. */
  monthToDate: MonthToDateSchema.nullable().optional(),
  rows: z.array(DemandMatrixRowSchema),
  /**
   * Null at rolled-up grains, where counting the grouped set costs as much as
   * producing it. `hasMore` is always exact — pair them for the pager.
   */
  totalRows: z.number().nullable(),
  hasMore: z.boolean(),
  /** Echoes the requested measure, so a stale page cannot mislabel its cells. */
  measure: DemandMeasureSchema,
  /**
   * Rows on this page whose reference unit cost is unknown. Only meaningful
   * when the measure is `cost`; the UI says so rather than showing a total that
   * quietly omits them.
   */
  rowsWithoutCost: z.number(),
});
export type GetDemandMatrixResponse = z.infer<
  typeof GetDemandMatrixResponseSchema
>;

// ─── Summary ────────────────────────────────────────────────────────────────

export const GetDemandSummaryInputSchema = DemandMatrixFilterSchema;
export type GetDemandSummaryInput = z.infer<typeof GetDemandSummaryInputSchema>;

export const GetDemandSummaryResponseSchema = z.object({
  runId: z.string().uuid().nullable(),
  asOfDate: z.string().nullable(),
  months: z.array(z.string()),
  /**
   * Projected quantity per month, split by demand class. The split is the point
   * of the chart: a month carried by 'intermittent' and 'lumpy' series is a
   * much softer number than the same total from 'smooth' ones.
   */
  monthly: z.array(
    z.object({
      month: z.string(),
      total: z.number(),
      byClass: z.record(z.string(), z.number()),
    }),
  ),
  totalQty: z.number(),
  /** Month with the largest projected quantity; null when the scope is empty. */
  peakMonth: z.string().nullable(),
  seriesCount: z.number(),
  skuCount: z.number(),
  warehouseCount: z.number(),
  /** Share of series in scope that fell back to a flat assumed lead time. */
  assumedLeadTimeShare: z.number().nullable(),
});
export type GetDemandSummaryResponse = z.infer<
  typeof GetDemandSummaryResponseSchema
>;

// ─── Filter options ─────────────────────────────────────────────────────────

export const GetDemandFilterOptionsInputSchema = z.object({
  runId: z.string().uuid().optional(),
});

export const GetDemandFilterOptionsResponseSchema = z.object({
  runId: z.string().uuid().nullable(),
  asOfDate: z.string().nullable(),
  /** Full horizon of the run, before any month-range filter. */
  months: z.array(z.string()),
  divisions: z.array(
    z.object({ id: z.string().uuid(), name: z.string().nullable() }),
  ),
  warehouses: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string().nullable(),
      /** The warehouse's Display Code, when its organization has one. */
      displayCode: z.string().nullable().default(null),
      divisionId: z.string().uuid().nullable(),
      type: z.string().nullable(),
    }),
  ),
  warehouseTypes: z.array(z.string()),
  /** Taken from the run manifest's own tallies, so these cost no scan. */
  demandClasses: z.array(z.string()),
  methods: z.array(z.string()),
  leadTimeSources: z.array(z.string()),
  /** From the policy table, which is where stocking intent actually lives. */
  stockStatuses: z.array(z.string()),
  popularityCodes: z.array(z.string()),
});
export type GetDemandFilterOptionsResponse = z.infer<
  typeof GetDemandFilterOptionsResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const forecastRunsContracts = {
  list: oc
    .input(ListForecastRunsInputSchema)
    .output(ListForecastRunsResponseSchema)
    .route({
      method: "GET",
      summary: "List demand forecast runs",
      description:
        "Returns recent demand-forecast runs with status, coverage and forecasting method and demand-class breakdowns. Filter by purpose or status.",
    }),
  get: oc
    .input(GetForecastRunInputSchema)
    .output(GetForecastRunResponseSchema)
    .route({
      method: "GET",
      summary: "Get a demand forecast run",
      description:
        "Returns one forecast run (latest production run by default) with per-division coverage, divisions that produced no forecast, and the total demand curve over the horizon.",
    }),
  listSeries: oc
    .input(ListForecastSeriesInputSchema)
    .output(ListForecastSeriesResponseSchema)
    .route({
      method: "GET",
      summary: "List series in a forecast run",
      description:
        "Paged SKU-warehouse forecast series with demand history, forward forecast and lead-time evidence. Filter by division, warehouse, search, demand class, method, lead-time source.",
    }),
  demandMatrix: oc
    .input(GetDemandMatrixInputSchema)
    .output(GetDemandMatrixResponseSchema)
    .route({
      method: "GET",
      summary: "Get the projected demand grid",
      description:
        "Projected demand by SKU, location and month in quantity or cost, rolled up by warehouse, division or SKU, with optional actuals or prior-run comparisons and many filters.",
    }),
  monthToDateDaily: oc
    .input(GetMonthToDateDailyInputSchema)
    .output(GetMonthToDateDailyResponseSchema)
    .route({
      method: "GET",
      summary: "Get one series' month-to-date by day",
      description:
        "Daily forecast and daily actual demand for one SKU at one warehouse across the current month, for comparing progress through the month.",
    }),
  demandSummary: oc
    .input(GetDemandSummaryInputSchema)
    .output(GetDemandSummaryResponseSchema)
    .route({
      method: "GET",
      summary: "Summarize projected demand",
      description:
        "Headline totals and monthly projected demand split by demand class (smooth, intermittent, lumpy) for the filtered forecast scope.",
    }),
  demandFilterOptions: oc
    .input(GetDemandFilterOptionsInputSchema)
    .output(GetDemandFilterOptionsResponseSchema)
    .route({
      method: "GET",
      summary: "Get projected demand filter options",
      description:
        "Returns the divisions, warehouses, demand classes, methods and other filter values available for a forecast run's demand grid, plus its month horizon.",
    }),
} as const;
